import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Users,
  Calendar,
  Award,
  FileText,
  MessageCircle,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyTeacherProfile } from "../../services/teachers";
import { getStudents } from "../../services/students";

const TeacherCourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  // Fetch teacher profile to get course details
  const { data: teacherData, isLoading: teacherLoading } = useQuery({
    queryKey: ["teacher-profile"],
    queryFn: () => getMyTeacherProfile(),
    enabled: !!user?.id,
  });

  // Fetch students
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ["students"],
    queryFn: () => getStudents(),
    enabled: !!user?.id,
  });

  if (teacherLoading || studentsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-emerald-500"></div>
      </div>
    );
  }

  const teacher = teacherData?.data;
  const allStudents = studentsData?.data || [];

  // Find the course and its associated class
  let courseInfo: any = null;
  let associatedClass: any = null;

  teacher?.currentClasses?.forEach((classAssignment: any) => {
    const course = classAssignment.courses?.find((c: any) => c._id === id);
    if (course) {
      courseInfo = course;
      associatedClass = classAssignment;
    }
  });

  if (!courseInfo || !associatedClass) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-red-200/60 shadow-xl p-8">
          <div className="flex items-start gap-4">
            <div className="bg-gradient-to-br from-red-300 to-pink-400 rounded-xl p-3 shadow-lg">
              <span className="text-white text-xl">⚠</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900">
                Course Not Found
              </h3>
              <p className="text-slate-600 mt-1 font-medium">
                You don't have access to this course or it doesn't exist.
              </p>
              <Link
                to="/teacher/courses"
                className="inline-flex items-center mt-4 gap-2 bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-4 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <ArrowLeft size={16} />
                Back to Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter students for the associated class
  const courseStudents = allStudents.filter(
    (student: any) => student.classId === associatedClass.classId._id,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-6">
        <Link
          to="/teacher/courses"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 font-semibold transition-colors"
        >
          <ArrowLeft size={18} />
          Back to Courses
        </Link>
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900">
              {courseInfo.name}
            </h1>
            <p className="text-slate-600 mt-2 font-medium">
              {courseInfo.fullName || courseInfo.name}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="bg-gradient-to-br from-blue-300 to-indigo-400 text-white text-sm px-3 py-1.5 rounded-xl font-bold shadow-sm">
                {courseInfo.category || "Course"}
              </span>
              <span className="bg-gradient-to-br from-emerald-300 to-emerald-400 text-white text-sm px-3 py-1.5 rounded-xl font-bold shadow-sm">
                {courseInfo.level || "Level"}
              </span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <Link
              to={`/teacher/courses/${courseInfo._id}/materials`}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-br from-emerald-300 to-emerald-400 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <FileText size={18} />
              Manage Materials
            </Link>
            <Link
              to={`/teacher/classes/${associatedClass.classId._id}`}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-br from-blue-300 to-indigo-400 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <Users size={18} />
              View Class
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Course Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-5">
              Course Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl p-2.5 shadow-lg">
                  <BookOpen className="text-white" size={20} />
                </div>
                <div>
                  <span className="text-sm text-slate-500 font-medium">
                    Category:
                  </span>
                  <p className="font-bold text-slate-900">
                    {courseInfo.category || "Not specified"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-amber-300 to-yellow-400 rounded-xl p-2.5 shadow-lg">
                  <Award className="text-white" size={20} />
                </div>
                <div>
                  <span className="text-sm text-slate-500 font-medium">
                    Credits:
                  </span>
                  <p className="font-bold text-slate-900">
                    {courseInfo.credits || 0} credits
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2.5 shadow-lg">
                  <Users className="text-white" size={20} />
                </div>
                <div>
                  <span className="text-sm text-slate-500 font-medium">
                    Class:
                  </span>
                  <p className="font-bold text-slate-900">
                    {associatedClass.classId.name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-xl p-2.5 shadow-lg">
                  <Calendar className="text-white" size={20} />
                </div>
                <div>
                  <span className="text-sm text-slate-500 font-medium">
                    Level:
                  </span>
                  <p className="font-bold text-slate-900">
                    {courseInfo.level || "Not specified"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Course Description */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              Course Description
            </h2>
            <div className="prose max-w-none">
              <p className="text-slate-600 font-medium leading-relaxed">
                {courseInfo.description ||
                  `This is the ${courseInfo.name} course for ${associatedClass.classId.name} class. 
                  It covers essential topics and skills needed for students at this level.`}
              </p>
            </div>
          </div>

          {/* Learning Objectives */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              Learning Objectives
            </h2>
            {courseInfo.objectives && courseInfo.objectives.length > 0 ? (
              <ul className="space-y-3">
                {courseInfo.objectives.map(
                  (objective: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-7 h-7 bg-gradient-to-br from-blue-300 to-indigo-400 text-white rounded-xl flex items-center justify-center text-sm font-bold shadow-sm flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="text-slate-700 font-medium flex-1">
                        {objective}
                      </span>
                    </li>
                  ),
                )}
              </ul>
            ) : (
              <div className="text-slate-600 font-medium">
                <p className="mb-3">Learning objectives for this course:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Understand core concepts and principles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Apply knowledge through practical exercises</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Develop critical thinking skills</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Demonstrate mastery through assessments</span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Enrolled Students */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-900">
                Enrolled Students ({courseStudents.length})
              </h2>
              <Link
                to={`/teacher/students?classId=${associatedClass.classId._id}`}
                className="text-blue-600 hover:text-blue-800 text-sm font-bold transition-colors"
              >
                View All Students
              </Link>
            </div>
            {courseStudents.length > 0 ? (
              <div className="space-y-3">
                {courseStudents.slice(0, 5).map((student: any) => (
                  <div
                    key={student._id}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-100 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-sm">
                          {(student.userInfo?.firstName ||
                            student.firstName ||
                            "U")[0].toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">
                          {student.userInfo?.firstName || student.firstName}{" "}
                          {student.userInfo?.lastName || student.lastName}
                        </h3>
                        <p className="text-sm text-slate-600 font-medium">
                          {student.studentId || student.rollNumber}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1.5 text-xs rounded-xl font-bold shadow-sm ${
                        student.status === "active"
                          ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                          : "bg-gradient-to-br from-slate-300 to-slate-400 text-white"
                      }`}
                    >
                      {student.status || "Active"}
                    </span>
                  </div>
                ))}
                {courseStudents.length > 5 && (
                  <p className="text-center text-slate-600 text-sm pt-2 font-medium">
                    And {courseStudents.length - 5} more students...
                  </p>
                )}
              </div>
            ) : (
              <p className="text-slate-600 font-medium">
                No students enrolled in this course yet.
              </p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-5">
              Course Stats
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600 font-medium">Credits:</span>
                <span className="font-bold text-slate-900">
                  {courseInfo.credits || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 font-medium">Level:</span>
                <span className="font-bold text-slate-900">
                  {courseInfo.level || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 font-medium">Category:</span>
                <span className="font-bold text-slate-900">
                  {courseInfo.category || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 font-medium">Students:</span>
                <span className="font-bold text-slate-900">
                  {courseStudents.length}
                </span>
              </div>
            </div>
          </div>

          {/* Course Materials */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">
              Course Materials
            </h3>
            <div className="space-y-3">
              {courseInfo.materials && courseInfo.materials.length > 0 ? (
                courseInfo.materials.map((material: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-100"
                  >
                    <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-1.5 shadow-sm">
                      <FileText className="text-white" size={16} />
                    </div>
                    <span className="text-sm text-slate-700 font-medium">
                      {material.name || `Material ${index + 1}`}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-slate-600 text-sm font-medium">
                  No materials uploaded yet.
                </p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <Link
                to={`/teacher/classes/${associatedClass.classId._id}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-br from-blue-300 to-indigo-400 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <Users size={18} />
                View Class
              </Link>
              <Link
                to={`/teacher/attendance?classId=${associatedClass.classId._id}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <Calendar size={18} />
                Take Attendance
              </Link>
              {courseInfo.whatsappGroupLink && (
                <a
                  href={courseInfo.whatsappGroupLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-br from-green-300 to-emerald-400 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <MessageCircle size={18} />
                  WhatsApp Group
                </a>
              )}
              {/* <Link
                to={`/teacher/assessments?classId=${associatedClass.classId._id}&courseId=${courseInfo._id}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <Award size={18} />
                Assessments
              </Link> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherCourseDetail;
